import {Component, Input, OnInit} from '@angular/core';
import {ContactDetailsResponse, MsisdnOwnerResponse, ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {DATE_UI_FORMAT, LocalizationService, normalizePhone} from '@npck/libs/commons';
import {applyLargeEllipsis, Column, ColumnType, Row, StatusColumn} from '../../../../../../../components/full-table/table/table.component';
import {mapMsisdnStatusToStyle} from '../../../../../../../components/status/status.component';
import {DictionaryHolderService, findValueInDictionary, resolveRefName} from '../../../../../../../domain/dictionary-holder.service';
import dayjs from 'dayjs';
import {forkJoin} from 'rxjs';

@Component({
    selector: 'af-contact-msisdn-info-tab',
    templateUrl: 'contact-msisdn-info-tab.component.html',
    standalone: false,
})
export class ContactMsisdnInfoTabComponent implements OnInit {
    @Input() contactDetails: ContactDetailsResponse;
    dataSource: Row[] = [];

    private msisdnStatuses: ReferenceBookResponse[] = [];
    private eventSources: ReferenceBookResponse[] = [];
    private countries: ReferenceBookResponse[] = [];

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        if (!this.contactDetails.owners?.length) {
            return;
        }
        forkJoin({
            msisdnStatuses: this.dictionaryHolderService.getReference(ReferenceBookType.MSISDN_STATUS),
            eventSources: this.dictionaryHolderService.getReference(ReferenceBookType.MOBILE_OPERATOR_EVENT_SOURCE),
            countries: this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
        }).subscribe(({msisdnStatuses, eventSources, countries}) => {
            this.msisdnStatuses = msisdnStatuses;
            this.eventSources = eventSources;
            this.countries = countries;
            this.dataSource = this.contactDetails.owners.map(owner => this.buildOwnerRow(owner));
        });
    }

    private buildOwnerRow(owner: MsisdnOwnerResponse): Row {
        const registeredMsisdns = (owner.registeredMsisdns || []).map(m => normalizePhone(m)).join(', ');
        const detectedBy = owner.detectedBy ? resolveRefName(this.eventSources, owner.detectedBy) : '';

        return <Row>{
            ownershipPeriod: <Column>{
                type: ColumnType.TEXT,
                text: this.formatOwnershipPeriod(owner.checkFromDate, owner.checkToDate),
            },
            msisdnStatus: <StatusColumn>{
                type: ColumnType.STATUS,
                text: owner.msisdnStatus ? resolveRefName(this.msisdnStatuses, owner.msisdnStatus) : '',
                style: mapMsisdnStatusToStyle(owner.msisdnStatus),
            },
            occurredDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: owner.occurredDate,
            },
            fullName: <Column>{
                type: ColumnType.TEXT,
                text: owner.fullName,
                ...applyLargeEllipsis(owner.fullName),
            },
            isResident: <Column>{
                type: ColumnType.TEXT,
                text: owner.isResident != null
                    ? this.localizationService.getLocalizedKey(
                        owner.isResident ? 'i18n_af_residentStatus#resident' : 'i18n_af_residentStatus#nonResident',
                    ) : '',
            },
            iin: <Column>{
                type: ColumnType.TEXT,
                text: owner.iin,
            },
            passportNumber: <Column>{
                type: ColumnType.TEXT,
                text: owner.passportNumber,
            },
            residencyCountry: <Column>{
                type: ColumnType.TEXT,
                text: this.resolveCountryName(owner.residencyCountry),
            },
            registeredMsisdns: <Column>{
                type: ColumnType.TEXT,
                text: registeredMsisdns,
                ...applyLargeEllipsis(registeredMsisdns),
            },
            detectedBy: <Column>{
                type: ColumnType.TEXT,
                text: detectedBy,
                ...applyLargeEllipsis(detectedBy),
            },
            activationDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: owner.activationDate,
            },
            terminationDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: owner.terminationDate,
            },
        };
    }

    private resolveCountryName(code: string): string {
        if (!code) {
            return '';
        }
        return findValueInDictionary(this.countries, code, 'name_ru') || code;
    }

    private formatOwnershipPeriod(fromDate: string, toDate: string): string {
        const from = fromDate ? dayjs(fromDate).format(DATE_UI_FORMAT) : '';
        const to = toDate ? dayjs(toDate).format(DATE_UI_FORMAT) : '';
        if (from && to) {
            return `${from} - ${to}`;
        }
        return from || to || '';
    }
}
